import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Card,
  useTheme,
  Radio,
  TextField,
  Autocomplete,
  InputAdornment,
  FormHelperText,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress
} from '@mui/material';
import { User, Stethoscope, ChevronLeft, Search, AlertCircle, Edit} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AppleSpinner from '../../common/AppleSpinner';
import agthealthPatientService from '../services/agthealthPatientService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Health color constant
const HEALTH_GREEN = '#4CAF50';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Mock health centers data - Será reemplazado por datos reales de la API
const mockHealthCenters = {
  us: [
    { id: 1, name: 'Mayo Clinic' },
    { id: 2, name: 'Cleveland Clinic' },
    { id: 3, name: 'Johns Hopkins Hospital' },
    { id: 4, name: 'Massachusetts General Hospital' },
    { id: 5, name: 'UCLA Medical Center' },
    { id: 6, name: 'New York-Presbyterian Hospital' },
    { id: 7, name: 'Stanford Health Care' },
    { id: 8, name: 'UCSF Medical Center' },
  ],
  cl: [
    { id: 1, name: 'Clínica Las Condes' },
    { id: 2, name: 'Hospital Clínico Universidad de Chile' },
    { id: 3, name: 'Clínica Alemana' },
    { id: 4, name: 'Hospital del Salvador' },
    { id: 5, name: 'Hospital Barros Luco Trudeau' },
    { id: 6, name: 'Clínica Santa María' },
    { id: 7, name: 'Hospital Sótero del Río' },
    { id: 8, name: 'Hospital San José' },
    { id: 9, name: 'Centro de Salud Familiar Rauco' },
  ]
};

// Mock professional data
const mockProfessionals = {
  us: [
    { userId: 'prof-1', firstName: 'John', lastName: 'Smith', displayName: 'Dr. John Smith', specialtyArea: 'Cardiology' },
    { userId: 'prof-2', firstName: 'Sarah', lastName: 'Johnson', displayName: 'Dr. Sarah Johnson', specialtyArea: 'Neurology' },
    { userId: 'prof-3', firstName: 'Michael', lastName: 'Brown', displayName: 'Dr. Michael Brown', specialtyArea: 'Dermatology' },
    { userId: 'prof-4', firstName: 'Emily', lastName: 'Davis', displayName: 'Dr. Emily Davis', specialtyArea: 'Pediatrics' },
  ],
  cl: [
    { userId: 'prof-5', firstName: 'Carlos', lastName: 'Rodríguez', displayName: 'Dr. Carlos Rodríguez', specialtyArea: 'Cardiología' },
    { userId: 'prof-6', firstName: 'Ana', lastName: 'Martínez', displayName: 'Dra. Ana Martínez', specialtyArea: 'Neurología' },
    { userId: 'prof-7', firstName: 'Javier', lastName: 'González', displayName: 'Dr. Javier González', specialtyArea: 'Dermatología' },
    { userId: 'prof-8', firstName: 'María', lastName: 'López', displayName: 'Dra. María López', specialtyArea: 'Pediatría' },
  ]
};

// Validation functions
const validateRUT = (rut) => {
  // Remove dots and ensure there's a dash
  if (!rut) return false;

  // Basic format validation
  const rutRegex = /^[0-9]{7,8}-[0-9kK]{1}$/;
  if (!rutRegex.test(rut)) return false;

  // Extract digits and verifier
  const [digits, verifier] = rut.split('-');
  let s = 0;
  let t = 2;

  // Calculate checksum
  for (let i = digits.length - 1; i >= 0; i--) {
    s = s + parseInt(digits.charAt(i)) * t;
    t = t === 7 ? 2 : t + 1;
  }

  const v = 11 - (s % 11);
  const calculatedVerifier = v === 11 ? '0' : v === 10 ? 'K' : v.toString();

  // Compare calculated verifier with provided one
  return calculatedVerifier.toLowerCase() === verifier.toLowerCase();
};

const validateUSID = (id) => {
  // Basic validation for SSN format (XXX-XX-XXXX)
  const ssnRegex = /^[0-9]{3}-[0-9]{2}-[0-9]{4}$/;
  
  // Basic validation for Medical ID (could be more flexible)
  const medicalIdRegex = /^[A-Za-z0-9]{6,}$/;
  
  return ssnRegex.test(id) || medicalIdRegex.test(id);
};

// Styled components
const PageContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '800px',
  minHeight: '100vh', // Changed from height to minHeight for better scrolling
  display: 'flex',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  flexDirection: 'column',
  justifyContent: 'center',
  overflowY: 'auto', // Added to ensure content is scrollable
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    justifyContent: 'flex-start', // Better for mobile scrolling
    paddingBottom: theme.spacing(6), // Added padding at bottom for mobile
  }
}));

const ConfigCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 2px 14px rgba(0,0,0,0.06)',
  overflow: 'visible', // Changed from hidden to visible to allow content to overflow properly
  padding: theme.spacing(4),
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
  maxWidth: '600px',
  width: '100%', // Added to ensure the card takes full width within its container
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '16px',
  boxShadow: 'none',
  marginTop: theme.spacing(4),
  backgroundColor: HEALTH_GREEN,
  '&:hover': {
    backgroundColor: '#3d8b40',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.5)' : 'rgba(76, 175, 80, 0.5)',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.2, 2),
    fontSize: '14px',
  }
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '16px',
  boxShadow: 'none',
  marginTop: theme.spacing(4),
  marginRight: theme.spacing(2),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  color: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.text
    : (theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'),
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.4)'
      : (theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED'),
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.2, 2),
    fontSize: '14px',
    marginRight: theme.spacing(1),
  }
}));

const UserTypeOption = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  border: selected
    ? `2px solid ${HEALTH_GREEN}`
    : theme.palette.actualMode === 'wallpaper'
      ? `2px solid ${WALLPAPER_COLORS.border}`
      : `2px solid transparent`,
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.4)'
      : (theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED'),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& > .MuiBox-root:first-of-type': {
      marginBottom: theme.spacing(1),
    }
  }
}));

const CountryOption = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
  border: selected
    ? `2px solid ${HEALTH_GREEN}`
    : theme.palette.actualMode === 'wallpaper'
      ? `2px solid ${WALLPAPER_COLORS.border}`
      : `2px solid transparent`,
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.4)'
      : (theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED'),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

const IconContainer = styled(Box)(({ theme, color = HEALTH_GREEN }) => ({
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? '#3A3A3C' : '#E8E8ED'),
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.text
    : (theme.palette.mode === 'dark' ? HEALTH_GREEN : color),
  marginRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.2),
    marginRight: theme.spacing(1.5),
  }
}));

const AppleRadio = styled(Radio)(({ theme }) => ({
  color: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.textSecondary
    : (theme.palette.mode === 'dark' ? '#636366' : '#8E8E93'),
  '&.Mui-checked': {
    color: HEALTH_GREEN,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.border
        : 'rgba(0, 0, 0, 0.23)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: HEALTH_GREEN,
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'inherit',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: HEALTH_GREEN,
  },
}));

// Health Plus Sign component
const HealthPlusSign = styled('span')({
  color: HEALTH_GREEN,
  fontSize: '24px',
  fontWeight: 700,
  marginLeft: '2px',
  position: 'relative',
  top: '-8px',
});

// Responsive wrapper for option content
const OptionContent = styled(Box)(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  }
}));

// Styled ToggleButtonGroup
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(0.5),
    borderRadius: 12,
    borderColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.border
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'),
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : 'transparent',
    color: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.text
      : 'inherit',
    '&.Mui-selected': {
      backgroundColor: HEALTH_GREEN,
      color: '#fff',
      '&:hover': {
        backgroundColor: '#3d8b40',
      }
    },
    '&:not(:first-of-type)': {
      borderRadius: 12,
    },
    '&:first-of-type': {
      borderRadius: 12,
    },
  }
}));

// Styled ToggleButton
const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  flex: 1,
  padding: theme.spacing(1.5),
  fontSize: '0.9rem',
  fontWeight: 500,
  color: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.text
    : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.4)'
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
  },
  '&.Mui-disabled': {
    color: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255, 255, 255, 0.4)'
      : theme.palette.text.disabled,
  }
}));

// Main component
const Config = ({ onUpdateUser, currentUser }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [userType, setUserType] = useState('');
  const [country, setCountry] = useState('');
  const [userId, setUserId] = useState('');
  const [userIdError, setUserIdError] = useState('');
  const [healthCenter, setHealthCenter] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [, setFilteredProfessionals] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [customCenterName, setCustomCenterName] = useState('');
  const [customCenterMessage, setCustomCenterMessage] = useState('');
  const [showNoRecordDialog, setShowNoRecordDialog] = useState(false);
  const [hasRegisteredRecord, setHasRegisteredRecord] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isVerifyingId, setIsVerifyingId] = useState(false);
  const [isVerifyingCenter, setIsVerifyingCenter] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [medicalRecordFound, setMedicalRecordFound] = useState(false);
  const [showCorrectionDialog, setShowCorrectionDialog] = useState(false);
  const [alternativeCenters, setAlternativeCenters] = useState([]);
  const [centersCache, setCentersCache] = useState({});
  const [isLoadingCenters, setIsLoadingCenters] = useState(false);
  const [verifiedIds, setVerifiedIds] = useState(new Set());
  const [verifiedCenters, setVerifiedCenters] = useState(new Set());
  const [loadedCountries, setLoadedCountries] = useState(new Set());
  const [profileChecked, setProfileChecked] = useState(false);
  const [apiCallsInProgress, setApiCallsInProgress] = useState(new Set());
  const [providerType, setProviderType] = useState('center'); // 'center' o 'professional'
  
  // Estados para la lógica de profesionales independientes
  const [userProfessionals, setUserProfessionals] = useState([]);
  const [hasProfessionals, setHasProfessionals] = useState(false);
  const [isProfessionalChecked, setIsProfessionalChecked] = useState(false);
  const [professionalsCache] = useState({});
  const [isLoadingProfessionals] = useState(false);

  // Ref for scrolling container
  const contentRef = useRef(null);

  // 1. Memoizar la función onUpdateUser si viene del padre
  const memoizedOnUpdateUser = useCallback((user) => {
    if (typeof onUpdateUser === 'function') {
      onUpdateUser(user);
    }
  }, [onUpdateUser]);

  // Generate steps based on user type - simplified for patient only
  const getSteps = useCallback(() => {
    return isMobile 
      ? ['User Type', 'Country', 'ID', 'Provider'] // Shorter labels for mobile
      : ['User Type', 'Country', 'Identification', 'Health Provider'];
  }, [isMobile]);

  // Get steps without step count
  const steps = useMemo(() => getSteps(), [getSteps]);

  // Utility function for API calls to prevent duplicates
  const makeApiCall = useCallback(async (apiKey, apiFunction) => {
    if (apiCallsInProgress.has(apiKey)) {
      console.log(`API call ${apiKey} already in progress, skipping...`);
      return;
    }
    
    setApiCallsInProgress(prev => new Set(prev).add(apiKey));
    
    try {
      return await apiFunction();
    } finally {
      setApiCallsInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(apiKey);
        return newSet;
      });
    }
  }, [apiCallsInProgress]);

  // Verificar si el usuario ya tiene un perfil configurado
  useEffect(() => {
    // Solo ejecutar si no hemos verificado el perfil todavía
    if (profileChecked) return;

    const checkExistingProfile = async () => {
      setIsLoading(true);
      try {
        const profileResponse = await agthealthPatientService.getProfile();
        
        if (profileResponse.success && profileResponse.data) {
          // El usuario ya tiene perfil, actualizar estado local
          const profile = profileResponse.data;
          setUserType('patient');
          setCountry(profile.countryCode);
          setUserId(profile.identificationNumber);
          setHasRegisteredRecord(profile.hasRegisteredRecord || false);
     
          // Determinar si es un centro o un profesional independiente
          const isProfessional = profile.providerType === 'professional';
          setProviderType(isProfessional ? 'professional' : 'center');
          
          // Buscar el proveedor en los datos locales
          if (isProfessional) {
            const professionalObj = mockProfessionals[profile.countryCode]?.find(
              p => p.userId === profile.providerId || p.displayName === profile.providerName
            );
            
            if (professionalObj) {
              setHealthCenter(professionalObj);
            } else if (profile.providerName) {
              setHealthCenter({
                userId: profile.providerId || `temp-${Date.now()}`,
                displayName: profile.providerName,
                type: 'professional',
                isTemporary: !profile.providerId
              });
            }
          } else {
            // Es un centro de salud
            const centerObj = mockHealthCenters[profile.countryCode]?.find(
              c => c.id === profile.healthCenterId || c.name === profile.healthCenterName
            );
            
            if (centerObj) {
              setHealthCenter(centerObj);
            } else if (profile.healthCenterName) {
              setHealthCenter({
                id: profile.healthCenterId || `temp-${Date.now()}`,
                name: profile.healthCenterName,
                type: 'center',
                isTemporary: !profile.healthCenterId
              });
            }
          }
     
          // Notificar al componente padre
          if (memoizedOnUpdateUser) {
            memoizedOnUpdateUser({
              userType: 'patient',
              country: profile.countryCode,
              userId: profile.identificationNumber,
              providerType: isProfessional ? 'professional' : 'center',
              providerName: isProfessional ? profile.providerName : profile.healthCenterName,
              providerId: isProfessional ? profile.providerId : profile.healthCenterId,
              hasRegisteredRecord: profile.hasRegisteredRecord
            });
          }
          
          setProfileChecked(true); // ✅ Marcar como verificado
        } else {
          // Intentar recuperar datos del localStorage como fallback
          const storedUserType = localStorage.getItem('healthUserType');
          const storedCountry = localStorage.getItem('healthUserCountry');
          const storedUserId = localStorage.getItem('healthUserId');
          const storedProviderType = localStorage.getItem('healthProviderType') || 'center';
          const storedProviderName = localStorage.getItem('healthProviderName') || localStorage.getItem('healthCenter');
          const storedHasRecord = localStorage.getItem('healthHasRecord') === 'true';
          
          if (storedUserType && storedCountry && storedUserId && storedProviderName) {
            setUserType(storedUserType);
            setCountry(storedCountry);
            setUserId(storedUserId);
            setProviderType(storedProviderType);
            setHasRegisteredRecord(storedHasRecord);
            
            // Buscar el proveedor en los datos locales
            if (storedProviderType === 'professional') {
              const professionalObj = mockProfessionals[storedCountry]?.find(
                p => p.displayName === storedProviderName
              );
              
              if (professionalObj) {
                setHealthCenter(professionalObj);
              } else if (storedProviderName) {
                setHealthCenter({
                  userId: `temp-${Date.now()}`,
                  displayName: storedProviderName,
                  type: 'professional',
                  isTemporary: true
                });
              }
            } else {
              // Es un centro de salud
              const centerObj = mockHealthCenters[storedCountry]?.find(
                c => c.name === storedProviderName
              );
              
              if (centerObj) {
                setHealthCenter(centerObj);
              } else if (storedProviderName) {
                setHealthCenter({
                  id: `temp-${Date.now()}`,
                  name: storedProviderName,
                  type: 'center',
                  isTemporary: true
                });
              }
            }
            
            // Notificar al componente padre
            if (memoizedOnUpdateUser) {
              memoizedOnUpdateUser({
                userType: storedUserType,
                country: storedCountry,
                userId: storedUserId,
                providerType: storedProviderType,
                providerName: storedProviderName,
                hasRegisteredRecord: storedHasRecord
              });
            }
            
            // Indicar que debemos redirigir
            setShouldRedirect(true);
          }
          
          setProfileChecked(true); // ✅ Marcar como verificado
        }
      } catch (error) {
        console.error('Error al verificar perfil existente:', error);
        
        // Intentar recuperar datos del localStorage como último recurso
        const storedUserType = localStorage.getItem('healthUserType');
        const storedCountry = localStorage.getItem('healthUserCountry');
        const storedUserId = localStorage.getItem('healthUserId');
        const storedProviderType = localStorage.getItem('healthProviderType') || 'center';
        const storedProviderName = localStorage.getItem('healthProviderName') || localStorage.getItem('healthCenter');
        const storedHasRecord = localStorage.getItem('healthHasRecord') === 'true';
        
        if (storedUserType && storedCountry && storedUserId && storedProviderName) {
          setUserType(storedUserType);
          setCountry(storedCountry);
          setUserId(storedUserId);
          setProviderType(storedProviderType);
          setHasRegisteredRecord(storedHasRecord);
          
          // Buscar el proveedor en los datos locales
          if (storedProviderType === 'professional') {
            const professionalObj = mockProfessionals[storedCountry]?.find(
              p => p.displayName === storedProviderName
            );
            
            if (professionalObj) {
              setHealthCenter(professionalObj);
            } else if (storedProviderName) {
              setHealthCenter({
                userId: `temp-${Date.now()}`,
                displayName: storedProviderName,
                type: 'professional',
                isTemporary: true
              });
            }
          } else {
            // Es un centro de salud
            const centerObj = mockHealthCenters[storedCountry]?.find(
              c => c.name === storedProviderName
            );
            
            if (centerObj) {
              setHealthCenter(centerObj);
            } else if (storedProviderName) {
              setHealthCenter({
                id: `temp-${Date.now()}`,
                name: storedProviderName,
                type: 'center',
                isTemporary: true
              });
            }
          }
          
          // Notificar al componente padre
          if (memoizedOnUpdateUser) {
            memoizedOnUpdateUser({
              userType: storedUserType,
              country: storedCountry,
              userId: storedUserId,
              providerType: storedProviderType,
              providerName: storedProviderName,
              hasRegisteredRecord: storedHasRecord
            });
          }
          
          // Indicar que debemos redirigir
          setShouldRedirect(true);
        }
        
        setProfileChecked(true); // ✅ Marcar como verificado incluso en error
      } finally {
        setIsLoading(false);
      }
    };
    
    checkExistingProfile();
  }, [profileChecked, memoizedOnUpdateUser]); // ✅ Solo depende de profileChecked y memoizedOnUpdateUser

  // Memoriza la función handleComplete para evitar recreaciones
  const handleComplete = useCallback(async () => {
    if (!userType || !country || !userId) return;
    if (!healthCenter) return;
    
    // Crear un identificador único para esta operación
    const apiKey = `complete_${userType}_${country}_${userId}_${healthCenter.id || healthCenter.userId}`;
    
    return makeApiCall(apiKey, async () => {
      setIsSubmitting(true);
      
      try {
        // Primero verificar si el ID ya está registrado
        const checkResponse = await agthealthPatientService.checkIdentificationExists({
          identificationNumber: userId,
          countryCode: country
        });
        
        if (checkResponse.exists) {
          setUserIdError(t('identification.error'));
          return;
        }
        
        // Preparar datos según el tipo de proveedor
        let profileData = {
          identificationNumber: userId,
          countryCode: country,
          hasRegisteredRecord: hasRegisteredRecord
        };

        // Si el usuario seleccionó "sin proveedor"
        if (healthCenter.id === 'no-center' || healthCenter.type === 'none') {
          profileData = {
            ...profileData,
            providerType: 'none',
            healthCenterId: 'no-center',
            healthCenterName: 'Sin centro asignado'
          };
        } else if (providerType === 'professional') {
          profileData = {
            ...profileData,
            providerType: 'professional',
            providerId: healthCenter.userId,
            providerName: healthCenter.displayName || `${healthCenter.firstName || ''} ${healthCenter.lastName || ''}`.trim(),
            specialtyArea: healthCenter.specialtyArea
          };
        } else {
          profileData = {
            ...profileData,
            providerType: 'center',
            healthCenterId: healthCenter.id,
            healthCenterName: healthCenter.name
          };
        }
        
        // Crear perfil de paciente
        const profileResponse = await agthealthPatientService.createProfile(profileData);
        
        if (profileResponse.success) {
          // Si no tiene registro, mostrar diálogo informativo
          if (!hasRegisteredRecord) {
            setShowNoRecordDialog(true);
          }
          
          // Preparar datos para actualizar
          let providerName, providerId, finalProviderType;

          if (healthCenter.id === 'no-center' || healthCenter.type === 'none') {
            providerName = 'Sin centro asignado';
            providerId = 'no-center';
            finalProviderType = 'none';
          } else if (providerType === 'professional') {
            providerName = healthCenter.displayName || `${healthCenter.firstName || ''} ${healthCenter.lastName || ''}`.trim();
            providerId = healthCenter.userId;
            finalProviderType = 'professional';
          } else {
            providerName = healthCenter.name;
            providerId = healthCenter.id;
            finalProviderType = 'center';
          }

          // Actualizar usuario local
          const updatedUser = {
            ...(currentUser || {}),
            userType: 'patient',
            country,
            userId,
            providerType: finalProviderType,
            providerName,
            providerId,
            hasRegisteredRecord
          };

          // Save to localStorage as backup
          localStorage.setItem('healthUserType', 'patient');
          localStorage.setItem('healthUserCountry', country);
          localStorage.setItem('healthUserId', userId);
          localStorage.setItem('healthProviderType', finalProviderType);
          localStorage.setItem('healthProviderName', providerName);
          localStorage.setItem('healthProviderId', providerId || '');
          localStorage.setItem('healthHasRecord', hasRegisteredRecord.toString());

          // Para mantener compatibilidad con código existente
          localStorage.setItem('healthCenter', providerName);
          
          // Notify parent component about the update
          if (memoizedOnUpdateUser) {
            memoizedOnUpdateUser(updatedUser);
          }
        }
      } catch (error) {
        console.error('Error al completar el registro:', error);
      } finally {
        setIsSubmitting(false);
      }
    });
  }, [userType, country, userId, healthCenter, hasRegisteredRecord, providerType, currentUser, memoizedOnUpdateUser, t, makeApiCall]);

  // Efecto para realizar la redirección cuando sea necesario
  useEffect(() => {
    if (shouldRedirect && !isLoading) {
      handleComplete();
    }
  }, [shouldRedirect, isLoading, handleComplete]);

  // Initialize from current user if available
  useEffect(() => {
    if (currentUser) {
      if (currentUser.userType) setUserType(currentUser.userType === 'doctor' ? 'doctor' : 'patient');
      if (currentUser.country) setCountry(currentUser.country);
      if (currentUser.userId) setUserId(currentUser.userId);
      if (currentUser.providerType) setProviderType(currentUser.providerType);
      if (currentUser.hasRegisteredRecord) setHasRegisteredRecord(currentUser.hasRegisteredRecord);
      
      if (currentUser.providerName && currentUser.country) {
        if (currentUser.providerType === 'professional') {
          const professionalObj = mockProfessionals[currentUser.country]?.find(
            p => p.displayName === currentUser.providerName
          );
          if (professionalObj) setHealthCenter(professionalObj);
        } else {
          const centerObj = mockHealthCenters[currentUser.country]?.find(
            c => c.name === currentUser.providerName
          );
          if (centerObj) setHealthCenter(centerObj);
        }
      }
    }
  }, [currentUser]);

  // 5. Memoizar los centros filtrados para evitar recálculos innecesarios
  const memoizedFilteredCenters = useMemo(() => {
    if (!country) return [];
    
    const centers = centersCache[country] || mockHealthCenters[country] || [];
    
    if (!searchTerm || searchTerm.length < 2) {
      return centers;
    }
    
    return centers.filter(center => 
      center.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [country, searchTerm, centersCache]);
  
  // Memoizar los profesionales filtrados
  const memoizedFilteredProfessionals = useMemo(() => {
    if (!country || !hasProfessionals) return [];
    
    // Si tenemos profesionales asociados al usuario, usar esos
    if (userProfessionals && userProfessionals.length > 0) {
      if (!searchTerm || searchTerm.length < 2) {
        return userProfessionals;
      }
      
      return userProfessionals.filter(professional => {
        const displayName = professional.displayName || `${professional.firstName || ''} ${professional.lastName || ''}`.trim();
        return displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               (professional.specialtyArea && professional.specialtyArea.toLowerCase().includes(searchTerm.toLowerCase()));
      });
    }
    
    // Si no tenemos profesionales asociados, usar los de caché o mock
    const professionals = professionalsCache[country] || mockProfessionals[country] || [];
    
    if (!searchTerm || searchTerm.length < 2) {
      return professionals;
    }
    
    return professionals.filter(professional => {
      const displayName = professional.displayName || `${professional.firstName || ''} ${professional.lastName || ''}`.trim();
      return displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
             (professional.specialtyArea && professional.specialtyArea.toLowerCase().includes(searchTerm.toLowerCase()));
    });
  }, [country, searchTerm, professionalsCache, hasProfessionals, userProfessionals]);

  // 6. Modificar el useEffect de carga de centros
  useEffect(() => {
    if (!country || isLoadingCenters || !providerType || providerType !== 'center') return;
    if (loadedCountries.has(`center_${country}`)) return;
    
    const apiKey = `loadCenters_${country}`;
    
    const loadHealthCenters = async () => {
      // Verificar caché primero
      if (centersCache[country]) {
        setFilteredCenters(centersCache[country]);
        setLoadedCountries(prev => new Set(prev).add(`center_${country}`));
        return;
      }
      
      // Si no hay caché, cargar desde localStorage o API
      const cachedData = localStorage.getItem(`healthCenters_${country}`);
      const cacheTimestamp = localStorage.getItem(`healthCenters_${country}_timestamp`);
      const now = Date.now();
      
      if (cachedData && cacheTimestamp && (now - parseInt(cacheTimestamp) < 3600000)) {
        try {
          const parsed = JSON.parse(cachedData);
          setFilteredCenters(parsed);
          setCentersCache(prev => ({ ...prev, [country]: parsed }));
          // Marcar este país como ya cargado
          setLoadedCountries(prev => new Set(prev).add(`center_${country}`));
          return;
        } catch (e) {
          console.error("Error parsing cached data:", e);
          // Continuar con la carga desde API si hay error al parsear
        }
      }
      
      // Si no hay caché válida, cargar desde API
      setIsLoadingCenters(true);
      
      return makeApiCall(apiKey, async () => {
        try {
          const response = await agthealthPatientService.getHealthCentersByCountry(country);
          
          if (response.success && response.data) {
            const centers = response.data;
            setFilteredCenters(centers);
            setCentersCache(prev => ({ ...prev, [country]: centers }));
            
            // Guardar en localStorage
            localStorage.setItem(`healthCenters_${country}`, JSON.stringify(centers));
            localStorage.setItem(`healthCenters_${country}_timestamp`, now.toString());
          } else {
            // Fallback a datos mock
            setFilteredCenters(mockHealthCenters[country] || []);
          }
        } catch (error) {
          console.error('Error al cargar centros de salud:', error);
          setFilteredCenters(mockHealthCenters[country] || []);
        } finally {
          setIsLoadingCenters(false);
          // Marcar este país como ya cargado
          setLoadedCountries(prev => new Set(prev).add(`center_${country}`));
        }
      });
    };
    
    loadHealthCenters();
  }, [country, isLoadingCenters, centersCache, loadedCountries, makeApiCall, providerType]);
  
  // 7. Usar un useEffect separado para la búsqueda con debounce
  useEffect(() => {
    if (!country || !providerType) return;
    
    // Solo hacer búsqueda en servidor si el término es suficientemente largo
    if (!searchTerm || searchTerm.length < 3) {
      if (providerType === 'center') {
        setFilteredCenters(memoizedFilteredCenters);
      } else {
        setFilteredProfessionals(memoizedFilteredProfessionals);
      }
      return;
    }
    
    // Si estamos en modo profesional y tenemos profesionales asociados al usuario,
    // no necesitamos hacer búsqueda en el servidor
    if (providerType === 'professional' && hasProfessionals && userProfessionals.length > 0) {
      setFilteredProfessionals(memoizedFilteredProfessionals);
      return;
    }
    
    const apiKey = providerType === 'center' 
      ? `searchCenters_${country}_${searchTerm}`
      : `searchProfessionals_${country}_${searchTerm}`;
    
    const timeoutId = setTimeout(async () => {
      return makeApiCall(apiKey, async () => {
        try {
          if (providerType === 'center') {
            const response = await agthealthPatientService.searchHealthCenters(country, searchTerm);
            if (response.success && response.data) {
              setFilteredCenters(response.data);
            }
          } else {
            const response = await agthealthPatientService.searchProfessionals(country, searchTerm);
            if (response.success && response.data) {
              setFilteredProfessionals(response.data);
            }
          }
        } catch (error) {
          console.error(`Error al buscar ${providerType === 'center' ? 'centros de salud' : 'profesionales'}:`, error);
          if (providerType === 'center') {
            setFilteredCenters(memoizedFilteredCenters);
          } else {
            setFilteredProfessionals(memoizedFilteredProfessionals);
          }
        }
      });
    }, 800); // ✅ Debounce más largo para reducir llamadas
    
    return () => clearTimeout(timeoutId);
  }, [country, searchTerm, providerType, memoizedFilteredCenters, memoizedFilteredProfessionals, makeApiCall, hasProfessionals, userProfessionals]);

  // Validate ID when country or ID changes
  useEffect(() => {
    if (!userId) {
      setUserIdError('');
      return;
    }

    if (country === 'cl') {
      if (!validateRUT(userId)) {
        setUserIdError(t('identification.cl.error'));
      } else {
        setUserIdError('');
      }
    } else if (country === 'us') {
      if (!validateUSID(userId)) {
        setUserIdError(t('identification.us.error'));
      } else {
        setUserIdError('');
      }
    }
  }, [country, userId, t]);

  // Scroll to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [activeStep]);

  // 4. Memoizar las funciones que hacen llamadas a API
  const handleVerifyIdentification = useCallback(async () => {
    if (!userId || userIdError) return;

    // Verificar si ya hemos verificado este ID
    if (verifiedIds.has(userId)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }

    const apiKey = `verifyId_${userId}`;

    setIsVerifyingId(true);

    return makeApiCall(apiKey, async () => {
      try {
        // PRIMERO: Verificar si el RUT ya está registrado en la tabla de perfiles
        console.log('🔍 Verificando si el RUT ya está registrado...');
        const checkResponse = await agthealthPatientService.checkIdentificationExists({
          identificationNumber: userId,
          countryCode: country
        });

        console.log('📋 Respuesta de verificación de RUT:', checkResponse);

        if (checkResponse.exists) {
          console.error('❌ El RUT ya está registrado en el sistema');
          setUserIdError(t('identification.error'));
          setIsVerifyingId(false);
          return; // NO avanzar al siguiente paso
        }

        console.log('✅ RUT disponible, verificando registros médicos...');

        // SEGUNDO: Verificar si existe un registro médico para esta identificación
        const response = await agthealthPatientService.checkMedicalRecord(userId);
       
        // Marcar este ID como verificado
        setVerifiedIds(prev => new Set(prev).add(userId));
       
        if (response.success && response.exists) {
          // El usuario tiene un registro médico en un centro de salud
          setMedicalRecordFound(true);
          setHealthCenter({
            id: response.patientData.healthCenterId,
            name: response.patientData.healthCenter,
            type: 'center'
          });
       
          // Guardar centros alternativos si existen
          if (response.patientData.alternativeCenters && response.patientData.alternativeCenters.length > 0) {
            setAlternativeCenters(response.patientData.alternativeCenters);
          }
       
          // Actualizar estado para mostrar que tiene registro
          setHasRegisteredRecord(true);
        } else {
          setMedicalRecordFound(false);
        }

        // Verificar si el usuario tiene profesionales independientes asociados
        try {
          const professionalResponse = await agthealthPatientService.checkUserProfessionals(userId);
          
          if (professionalResponse.success && professionalResponse.hasProfessionals) {
            setHasProfessionals(true);
            setUserProfessionals(professionalResponse.professionals || []);
            
            // Si solo hay un profesional, seleccionarlo automáticamente
            if (professionalResponse.professionals.length === 1) {
              const professional = professionalResponse.professionals[0];
              setHealthCenter({
                ...professional,
                type: 'professional'
              });
              setProviderType('professional');
              setHasRegisteredRecord(true);
            }
          } else {
            setHasProfessionals(false);
            setUserProfessionals([]);
          }
        } catch (error) {
          console.error('Error al verificar profesionales independientes:', error);
          setHasProfessionals(false);
          setUserProfessionals([]);
        } finally {
          setIsProfessionalChecked(true);
        }
       
        // Avanzar al siguiente paso DESPUÉS de completar la verificación
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
       
      } catch (error) {
        console.error('Error al verificar identificación:', error);
        // No avanzamos automáticamente en caso de error
        setIsProfessionalChecked(true);
      } finally {
        setIsVerifyingId(false);
      }
    });
  }, [userId, userIdError, verifiedIds, makeApiCall, country, t]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Verificar si el usuario tiene registro en el centro seleccionado
  const handleSelectHealthCenter = useCallback(async (center) => {
    setHealthCenter(center);
    
    if (!center) return;
    
    // Solo verificar registros para centros de salud, no para profesionales independientes
    if (providerType !== 'center') {
      return;
    }
    
    // Crear un ID único para este centro y usuario
    const verificationKey = `${userId}_${center.id}`;
    
    // Verificar si ya hemos verificado este centro para este usuario
    if (verifiedCenters.has(verificationKey)) {
      return;
    }
    
    const apiKey = `verifyCenter_${userId}_${center.id}`;
    
    setIsVerifyingCenter(true);
    
    return makeApiCall(apiKey, async () => {
      try {
        // Verificar si el usuario tiene registro en este centro
        const response = await agthealthPatientService.checkCenterRecord({
          identificationNumber: userId,
          healthCenterId: center.id
        });
        
        // Marcar este centro como verificado para este usuario
        setVerifiedCenters(prev => new Set(prev).add(verificationKey));
        
        if (response.success) {
          setHasRegisteredRecord(response.hasRecord);
        }
      } catch (error) {
        console.error('Error al verificar centro:', error);
        setHasRegisteredRecord(false);
      } finally {
        setIsVerifyingCenter(false);
      }
    });
  }, [userId, verifiedCenters, makeApiCall, providerType]);

  // Manejar selección de profesional independiente
  const handleSelectProfessional = useCallback((professional) => {
    setHealthCenter({
      ...professional,
      type: 'professional'
    });
    // Para profesionales independientes asociados al usuario, asumimos que siempre hay registro
    setHasRegisteredRecord(true);
  }, []);

  // Abrir diálogo para corregir el centro detectado automáticamente
  const handleOpenCorrectionDialog = useCallback(() => {
    setShowCorrectionDialog(true);
  }, []);

  // Seleccionar un centro alternativo
  const handleSelectAlternativeCenter = useCallback((center) => {
    setHealthCenter(center);
    setShowCorrectionDialog(false);
    
    // Verificar si tiene registro en este centro
    handleSelectHealthCenter(center);
  }, [handleSelectHealthCenter]);

  // Manejar cambio en el tipo de proveedor
  const handleProviderTypeChange = useCallback((event, newProviderType) => {
    if (newProviderType !== null) {
      setProviderType(newProviderType);
      
      // Si cambia a profesional y solo hay uno, seleccionarlo automáticamente
      if (newProviderType === 'professional' && userProfessionals.length === 1) {
        const professional = userProfessionals[0];
        setHealthCenter({
          ...professional,
          type: 'professional'
        });
        setHasRegisteredRecord(true);
      } else {
        setHealthCenter(null);
      }
      
      setSearchTerm('');
    }
  }, [userProfessionals]);

  const handleNext = useCallback(() => {
    // Si el usuario es doctor, abre una nueva pestaña con health.agtapp.com
    if (userType === 'doctor') {
      window.open('https://health.agtapp.com', '_blank');
      return;
    }
   
    // Para pacientes, continuamos con el flujo normal
    // Validate before proceeding
    if (activeStep === 2) {
      if (country === 'cl' && !validateRUT(userId)) {
        setUserIdError(t('identification.cl.error'));
        return;
      } else if (country === 'us' && !validateUSID(userId)) {
        setUserIdError(t('identification.us.error'));
        return;
      }
   
      // Si la validación es correcta y no estamos ya verificando, iniciar verificación
      if (!isVerifyingId) {
        handleVerifyIdentification();
        return; // Importante: retornar aquí para evitar avanzar el paso dos veces
      }
    }
   
    // Para el paso 3 (Health Provider), solo verificamos que se haya seleccionado un proveedor
    if (activeStep === 3) {
      if (!healthCenter) {
        return; // No avanzar si no hay proveedor seleccionado
      }
    }
   
    // Avanzar al siguiente paso
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }, [activeStep, country, handleVerifyIdentification, healthCenter, isVerifyingId, t, userId, userType]);

  const handleBack = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }, []);

  const handleRequestCenter = useCallback(() => {
    setRequestError('');
    setShowRequestDialog(true);
  }, []);

  const handleSubmitRequest = useCallback(async () => {
    if (!customCenterName) return;
  
    const apiKey = `requestCenter_${customCenterName}`;
  
    setIsSubmitting(true);
    setRequestError('');
  
    return makeApiCall(apiKey, async () => {
      try {
        // Incluir explícitamente el ID de usuario y el código de país
        const response = await agthealthPatientService.requestCenterLink({
          centerName: customCenterName,
          customMessage: customCenterMessage,
          identificationNumber: userId,  // Añadir el número de identificación
          countryCode: country           // Añadir el código del país
        });
      
        if (response.success) {
          // Crear un centro temporal para la UI
          const tempCenter = {
            id: response.request?.requestId || `temp-${Date.now()}`,
            name: customCenterName,
            type: 'center',
            isTemporary: true
          };
      
          setHealthCenter(tempCenter);
          setShowRequestDialog(false);
      
          // Mostrar mensaje de éxito
          setShowSuccessDialog(true);
        } else {
          setRequestError(t('requestCenter.dialog.error'));
        }
      } catch (error) {
        console.error('Error al solicitar centro:', error);
        setRequestError(t('requestCenter.dialog.error'));
      } finally {
        setIsSubmitting(false);
      }
    });
  }, [country, customCenterMessage, customCenterName, makeApiCall, t, userId]);

  // Determine if the current step is complete
  const isStepComplete = useCallback(() => {
    switch (activeStep) {
      case 0: return !!userType;
      case 1: return !!country;
      case 2: return !!userId && !userIdError && !isVerifyingId;
      case 3: return !!healthCenter && !isVerifyingCenter;
      default: return false;
    }
  }, [activeStep, country, healthCenter, isVerifyingCenter, isVerifyingId, userId, userIdError, userType]);

  // Si está cargando, mostrar spinner
  if (isLoading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <AppleSpinner sizeDesktop={48} sizeMobile={36} />
        </Box>
      </PageContainer>
    );
  }

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mb: 4 }}>
            <UserTypeOption 
              selected={userType === 'patient'} 
              onClick={() => setUserType('patient')}
            >
              <IconContainer>
                <User size={isMobile ? 20 : 24} />
              </IconContainer>
              <OptionContent>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight={500}
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                  }}
                >
                  {t('userType.patient.title')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper'
                      ? WALLPAPER_COLORS.textSecondary
                      : 'text.secondary'
                  }}
                >
                  {t('userType.patient.description')}
                </Typography>
              </OptionContent>
              <AppleRadio 
                checked={userType === 'patient'}
                onChange={() => setUserType('patient')}
              />
            </UserTypeOption>

            <UserTypeOption 
              selected={userType === 'doctor'} 
              onClick={() => setUserType('doctor')}
            >
              <IconContainer>
                <Stethoscope size={isMobile ? 20 : 24} />
              </IconContainer>
              <OptionContent>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight={500}
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                  }}
                >
                  {t('userType.doctor.title')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper'
                      ? WALLPAPER_COLORS.textSecondary
                      : 'text.secondary'
                  }}
                >
                  {t('userType.doctor.description')}
                </Typography>
              </OptionContent>
              <AppleRadio 
                checked={userType === 'doctor'}
                onChange={() => setUserType('doctor')}
              />
            </UserTypeOption>
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ mb: 4 }}>
            <CountryOption 
              selected={country === 'us'} 
              onClick={() => setCountry('us')}
            >
              <IconContainer>
                <Box component="span" sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.2rem' }}>US</Box>
              </IconContainer>
              <OptionContent>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight={500}
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                  }}
                >
                  {t('country.us.title')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper'
                      ? WALLPAPER_COLORS.textSecondary
                      : 'text.secondary'
                  }}
                >
                  {t('country.us.description')}
                </Typography>
              </OptionContent>
              <AppleRadio 
                checked={country === 'us'}
                onChange={() => setCountry('us')}
              />
            </CountryOption>

            <CountryOption 
              selected={country === 'cl'} 
              onClick={() => setCountry('cl')}
            >
              <IconContainer>
                <Box component="span" sx={{ fontWeight: 'bold', fontSize: isMobile ? '1rem' : '1.2rem' }}>CL</Box>
              </IconContainer>
              <OptionContent>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight={500}
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                  }}
                >
                  {t('country.cl.title')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper'
                      ? WALLPAPER_COLORS.textSecondary
                      : 'text.secondary'
                  }}
                >
                  {t('country.cl.description')}
                </Typography>
              </OptionContent>
              <AppleRadio 
                checked={country === 'cl'}
                onChange={() => setCountry('cl')}
              />
            </CountryOption>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
              }}
            >
              {country === 'cl'
                ? t('identification.cl.label')
                : t('identification.us.label')}
            </Typography>
            <StyledTextField
              fullWidth
              label={country === 'cl' ? t('identification.cl.placeholder') : t('identification.us.placeholder')}
              variant="outlined"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder={country === 'cl' ? t('identification.cl.placeholder') : t('identification.us.placeholder')}
              error={!!userIdError}
              helperText={userIdError}
              disabled={isVerifyingId}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: theme.palette.actualMode === 'wallpaper'
                  ? WALLPAPER_COLORS.textSecondary
                  : 'text.secondary'
              }}
            >
              {country === 'cl'
                ? t('identification.cl.example')
                : t('identification.us.example')}
            </Typography>
          </Box>
        );
      
      case 3:
        return (
          <Box sx={{ mb: 4 }}>
            {medicalRecordFound && (
              <Alert
                sx={{ 
                  mt: 2, 
                  mb: 4,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(227, 227, 227, 0.5)',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  padding: 0
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                }}>
                  <span style={{
                    fontWeight: 500,
                    fontSize: '15px',
                    color: '#000000'
                  }}>
                    {t('healthCenter.information')}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '16px 20px'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: '#333333'
                  }}>
                    {t('healthCenter.automaticSelection')}
                  </p>
                  <IconButton 
                    size="small" 
                    onClick={handleOpenCorrectionDialog}
                    style={{ marginLeft: '8px' }}
                  >
                    <Edit size={18} />
                  </IconButton>
                </div>
              </Alert>
            )}
            
            {/* Selector de tipo de proveedor */}
            {!medicalRecordFound && (
              <StyledToggleButtonGroup
                value={providerType}
                exclusive
                onChange={handleProviderTypeChange}
                aria-label="Tipo de proveedor de salud"
                sx={{ mb: 2 }}
              >
                <StyledToggleButton value="center" aria-label="Centros de Salud">
                  {t('providerType.center')}
                </StyledToggleButton>
                <StyledToggleButton 
                  value="professional" 
                  aria-label="Profesionales Independientes"
                  disabled={!isProfessionalChecked || !hasProfessionals}
                >
                  {t('providerType.professional')}
                  {!isProfessionalChecked && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </StyledToggleButton>
              </StyledToggleButtonGroup>
            )}

            <Typography
              variant="body1"
              sx={{
                mb: 2,
                color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
              }}
            >
              {medicalRecordFound
                ? t('healthCenter.detected')
                : providerType === 'center'
                  ? t('healthCenter.select')
                  : t('healthProfessional.select')}
            </Typography>
            
            {providerType === 'center' ? (
              <Autocomplete
                value={healthCenter && healthCenter.type === 'center' ? healthCenter : null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleSelectHealthCenter({...newValue, type: 'center'});
                  } else {
                    setHealthCenter(null);
                  }
                }}
                inputValue={searchTerm}
                onInputChange={(event, newInputValue) => {
                  setSearchTerm(newInputValue);
                }}
                options={filteredCenters}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id || option.centerId === value.centerId}
                disabled={medicalRecordFound || isVerifyingCenter}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label={t('healthCenter.searchPlaceholder')}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={20} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {isVerifyingCenter && <AppleSpinner sizeDesktop={20} sizeMobile={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: '180px', // Limit height for mobile
                  }
                }}
                noOptionsText={
                  <Box sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('healthCenter.notFound')}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={handleRequestCenter}
                      sx={{ 
                        borderRadius: 10, 
                        textTransform: 'none',
                        borderColor: HEALTH_GREEN,
                        color: HEALTH_GREEN,
                        '&:hover': {
                          borderColor: HEALTH_GREEN,
                          backgroundColor: 'rgba(76, 175, 80, 0.04)',
                        }
                      }}
                    >
                      {t('healthCenter.requestNew')}
                    </Button>
                  </Box>
                }
              />
            ) : (
              // Selector de profesionales independientes
              <>
                {userProfessionals.length === 1 ? (
                  <Box sx={{ mb: 3 }}>
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? WALLPAPER_COLORS.bg
                          : undefined,
                        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                        color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined,
                        '& .MuiAlert-icon': {
                          color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
                        }
                      }}
                    >
                      {t('professionalAssociation.singleProfessional')}
                    </Alert>

                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        border: '1px solid',
                        borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'divider',
                        borderRadius: 2,
                        backgroundColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.bgLight : 'transparent'
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={500}
                        sx={{
                          color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                        }}
                      >
                        {userProfessionals[0].displayName || `${userProfessionals[0].firstName || ''} ${userProfessionals[0].lastName || ''}`.trim()}
                      </Typography>
                      {userProfessionals[0].specialtyArea && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.actualMode === 'wallpaper'
                              ? WALLPAPER_COLORS.textSecondary
                              : 'text.secondary'
                          }}
                        >
                          {userProfessionals[0].specialtyArea}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Autocomplete
                    value={healthCenter && healthCenter.type === 'professional' ? healthCenter : null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleSelectProfessional({...newValue, type: 'professional'});
                      } else {
                        setHealthCenter(null);
                      }
                    }}
                    inputValue={searchTerm}
                    onInputChange={(event, newInputValue) => {
                      setSearchTerm(newInputValue);
                    }}
                    options={userProfessionals} // Usar solo los profesionales asociados al usuario
                    getOptionLabel={(option) => {
                      return option.displayName || `${option.firstName || ''} ${option.lastName || ''}`.trim();
                    }}
                    isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={t('professionalAssociation.searchPlaceholder')}
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search size={20} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {isLoadingProfessionals && <AppleSpinner sizeDesktop={20} sizeMobile={16} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body1">
                            {option.displayName || `${option.firstName || ''} ${option.lastName || ''}`.trim()}
                          </Typography>
                          {option.specialtyArea && (
                            <Typography variant="body2" color="text.secondary">
                              {option.specialtyArea}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: '180px',
                      }
                    }}
                    noOptionsText={
                      <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Typography variant="body2">
                          {t('professionalAssociation.noProfessionalsFound')}
                        </Typography>
                      </Box>
                    }
                  />
                )}

                {/* Información para continuar sin profesional */}
                {!healthCenter && userProfessionals.length > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? WALLPAPER_COLORS.bg
                          : undefined,
                        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                        color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined,
                        '& .MuiAlert-icon': {
                          color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
                        }
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.actualMode === 'wallpaper'
                            ? WALLPAPER_COLORS.text
                            : 'inherit'
                        }}
                      >
                        {t('healthCenter.continueWithoutProvider', 'Puedes continuar sin seleccionar un centro de salud o profesional. Podrás agregarlo más tarde desde tu perfil.')}
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </>
            )}

            {!healthCenter && !medicalRecordFound && providerType === 'center' && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <FormHelperText sx={{ ml: 1 }}>
                    {t('healthCenter.startTyping')}
                  </FormHelperText>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleRequestCenter}
                    sx={{
                      textTransform: 'none',
                      color: HEALTH_GREEN,
                      '&:hover': {
                        backgroundColor: 'rgba(76, 175, 80, 0.04)',
                      }
                    }}
                  >
                    {t('healthCenter.cantFind')}
                  </Button>
                </Box>

                {/* Información para continuar sin centro */}
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? WALLPAPER_COLORS.bg
                      : undefined,
                    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined,
                    '& .MuiAlert-icon': {
                      color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper'
                        ? WALLPAPER_COLORS.text
                        : 'inherit'
                    }}
                  >
                    {t('healthCenter.continueWithoutProvider', 'Puedes continuar sin seleccionar un centro de salud o profesional. Podrás agregarlo más tarde desde tu perfil.')}
                  </Typography>
                </Alert>
              </Box>
            )}
            
            {healthCenter && healthCenter.type === 'center' && !hasRegisteredRecord && !healthCenter.isTemporary && healthCenter.id !== 'no-center' && (
              <Alert
                sx={{
                  mt: 4,
                  mb: 4,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(227, 227, 227, 0.5)',
                  overflow: 'hidden',
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? WALLPAPER_COLORS.bg
                    : '#fff',
                  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
                  padding: 0
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                }}>
                  <span style={{
                    fontWeight: 500,
                    fontSize: '15px',
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : '#000000'
                  }}>
                    {t('healthCenter.information')}
                  </span>
                </div>
                <div style={{
                  padding: '16px 20px'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : '#333333'
                  }}>
                    {country === 'cl'
                      ? t('noMedicalRecord.alert.cl', { center: healthCenter.name })
                      : t('noMedicalRecord.alert.us', { center: healthCenter.name })
                    }
                  </p>
                </div>
              </Alert>
            )}
            
            {healthCenter && healthCenter.isTemporary && (
              <Alert
                sx={{ 
                  mt: 4, 
                  mb: 4,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(227, 227, 227, 0.5)',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  padding: 0
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                }}>
                  <span style={{
                    fontWeight: 500,
                    fontSize: '15px',
                    color: '#000000'
                  }}>
                    {t('healthCenter.information')}
                  </span>
                </div>
                <div style={{
                  padding: '16px 20px'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: 1.5,
                    color: '#333333'
                  }}>
                    {country === 'cl'
                      ? t('temporaryCenter.alert.cl')
                      : t('temporaryCenter.alert.us')}
                  </p>
                </div>
              </Alert>
            )}
          </Box>
        );
      
      default:
        return 'Unknown step';
    }
  };

  return (
    <PageContainer
      ref={contentRef}
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <ConfigCard>
        <Box
          component={motion.div}
          variants={itemVariants}
          sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          mb: isMobile ? 2 : 4, 
          textAlign: 'left' 
        }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 600,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
            }}
          >
            {t('userType.title')}<HealthPlusSign>+</HealthPlusSign>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.textSecondary
                : 'text.secondary'
            }}
          >
            {activeStep === 0 && t('userType.subtitle')}
            {activeStep === 1 && t('country.title')}
            {activeStep === 2 && t('identification.title')}
            {activeStep === 3 && (providerType === 'center' ? t('healthCenter.title') : t('healthProfessional.title'))}
          </Typography>
        </Box>

        {getStepContent()}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          flexDirection: isMobile && activeStep === 0 ? 'column' : 'row'
        }}>
          {activeStep > 0 && (
            <BackButton
              startIcon={<ChevronLeft size={16} />}
              onClick={handleBack}
              disabled={isSubmitting || isVerifyingId || isVerifyingCenter}
              fullWidth={isMobile && activeStep === 0}
            >
              {t('buttons.back')}
            </BackButton>
          )}
          
          {activeStep === steps.length - 1 ? (
            // Si no hay healthCenter seleccionado, mostrar botón "Continuar sin proveedor"
            !healthCenter ? (
              <ActionButton
                variant="contained"
                disableElevation
                disabled={isSubmitting || isVerifyingId || isVerifyingCenter}
                onClick={async () => {
                  // Ejecutar registro sin proveedor con validaciones primero
                  console.log('🔍 Botón Continuar sin proveedor - Verificando datos:', {
                    userType,
                    country,
                    userId,
                    healthCenter
                  });

                  if (!userType || !country || !userId) {
                    console.error('❌ Faltan datos requeridos:', {
                      userType: !!userType,
                      country: !!country,
                      userId: !!userId
                    });
                    return;
                  }

                  console.log('✅ Datos válidos, iniciando registro sin proveedor...');
                  setIsSubmitting(true);

                  try {
                    const checkResponse = await agthealthPatientService.checkIdentificationExists({
                      identificationNumber: userId,
                      countryCode: country
                    });

                    console.log('📋 Respuesta de verificación de ID:', checkResponse);

                    if (checkResponse.exists) {
                      console.error('❌ La identificación ya existe');
                      setUserIdError(t('identification.error'));
                      setIsSubmitting(false);
                      return;
                    }

                    const noProviderCenter = {
                      id: 'no-center',
                      name: 'Sin centro asignado',
                      type: 'none',
                      isTemporary: false
                    };

                    const profileData = {
                      identificationNumber: userId,
                      countryCode: country,
                      hasRegisteredRecord: false,
                      providerType: 'none',
                      healthCenterId: 'no-center',
                      healthCenterName: 'Sin centro asignado'
                    };

                    console.log('📤 Creando perfil con datos:', profileData);
                    const profileResponse = await agthealthPatientService.createProfile(profileData);
                    console.log('📥 Respuesta de creación de perfil:', profileResponse);

                    if (profileResponse.success) {
                      console.log('✅ Perfil creado exitosamente, actualizando estado...');
                      setHealthCenter(noProviderCenter);
                      setHasRegisteredRecord(false);

                      const updatedUser = {
                        ...(currentUser || {}),
                        userType: 'patient',
                        country,
                        userId,
                        providerType: 'none',
                        providerName: 'Sin centro asignado',
                        providerId: 'no-center',
                        hasRegisteredRecord: false
                      };

                      console.log('💾 Guardando en localStorage:', updatedUser);
                      localStorage.setItem('healthUserType', 'patient');
                      localStorage.setItem('healthUserCountry', country);
                      localStorage.setItem('healthUserId', userId);
                      localStorage.setItem('healthProviderType', 'none');
                      localStorage.setItem('healthProviderName', 'Sin centro asignado');
                      localStorage.setItem('healthProviderId', 'no-center');
                      localStorage.setItem('healthHasRecord', 'false');
                      localStorage.setItem('healthCenter', 'Sin centro asignado');

                      console.log('🔄 Llamando a memoizedOnUpdateUser...');
                      if (memoizedOnUpdateUser) {
                        memoizedOnUpdateUser(updatedUser);
                      }
                      console.log('✅ Proceso completado!');
                    } else {
                      console.error('❌ Error: profileResponse.success es false:', profileResponse);
                    }
                  } catch (error) {
                    console.error('❌ Error al completar el registro:', error);
                  } finally {
                    console.log('🔚 Finalizando, reseteando isSubmitting');
                    setIsSubmitting(false);
                  }
                }}
                fullWidth={isMobile || activeStep === 0}
                sx={{
                  color: '#FFFFFF',
                }}
              >
                {isSubmitting ?
                  <AppleSpinner sizeDesktop={24} sizeMobile={20} /> :
                  t('healthCenter.continueWithout', 'Continuar sin proveedor')
                }
              </ActionButton>
            ) : (
              // Si hay healthCenter seleccionado, mostrar botón "Complete"
              <ActionButton
                variant="contained"
                disableElevation
                disabled={
                  !isStepComplete() ||
                  isSubmitting ||
                  isVerifyingId ||
                  isVerifyingCenter ||
                  (healthCenter && healthCenter.isTemporary) ||
                  (healthCenter && healthCenter.type === 'center' && !hasRegisteredRecord && !healthCenter.isTemporary && healthCenter.id !== 'no-center')
                }
                onClick={handleComplete}
                fullWidth={isMobile || activeStep === 0}
                sx={{
                  color: '#FFFFFF',
                }}
              >
                {isSubmitting ?
                  <AppleSpinner sizeDesktop={24} sizeMobile={20} /> :
                  (healthCenter && healthCenter.isTemporary) ?
                    t('buttons.requestSent') :
                    (healthCenter && healthCenter.type === 'center' && !hasRegisteredRecord && !healthCenter.isTemporary && healthCenter.id !== 'no-center') ?
                      t('buttons.noMedicalRecord') :
                      t('buttons.complete')
                }
              </ActionButton>
            )
          ) : (
            <ActionButton 
              variant="contained"
              disableElevation
              disabled={!isStepComplete() || isVerifyingId || isVerifyingCenter}
              onClick={handleNext}
              fullWidth={isMobile || activeStep === 0}
            >
              {isVerifyingId ? <AppleSpinner sizeDesktop={24} sizeMobile={20} /> : t('buttons.continue')}
            </ActionButton>
          )}
        </Box>
      </ConfigCard>

      {/* Dialog for requesting a new center */}
      <Dialog
        open={showRequestDialog}
        onClose={() => !isSubmitting && setShowRequestDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bg
              : undefined,
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
          }
        }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
          }}
        >
          {t('requestCenter.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              mb: 2,
              color: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.textSecondary
                : undefined
            }}
          >
            {t('requestCenter.dialog.description')}
          </DialogContentText>
          {requestError && (
            <Alert
              sx={{ 
                mb: 2,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(227, 227, 227, 0.5)',
                overflow: 'hidden',
                backgroundColor: '#fff',
                padding: 0
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
              }}>
                <span style={{
                  fontWeight: 500,
                  fontSize: '15px',
                  color: '#000000'
                }}>
                  {t('requestCenter.dialog.error')}
                </span>
              </div>
              <div style={{
                padding: '16px 20px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: 1.5,
                  color: '#333333'
                }}>
                  {requestError}
                </p>
              </div>
            </Alert>
          )}
          <StyledTextField
            autoFocus
            label={t('requestCenter.dialog.centerName')}
            fullWidth
            variant="outlined"
            value={customCenterName}
            onChange={(e) => setCustomCenterName(e.target.value)}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />
          <StyledTextField
            label={t('requestCenter.dialog.additionalInfo')}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={customCenterMessage}
            onChange={(e) => setCustomCenterMessage(e.target.value)}
            disabled={isSubmitting}
            placeholder={t('requestCenter.dialog.additionalInfoPlaceholder')}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setShowRequestDialog(false)} 
            disabled={isSubmitting}
            sx={{ 
              textTransform: 'none',
              color: theme.palette.text.primary
            }}
          >
            {t('requestCenter.dialog.cancel')}
          </Button>
          <Button 
            onClick={handleSubmitRequest} 
            variant="contained"
            disabled={!customCenterName || isSubmitting}
            sx={{ 
              textTransform: 'none',
              backgroundColor: HEALTH_GREEN,
              '&:hover': {
                backgroundColor: '#3d8b40',
              },
              borderRadius: 10,
            }}
          >
            {isSubmitting ? <AppleSpinner sizeDesktop={20} sizeMobile={16} /> : t('requestCenter.dialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for no medical record */}
      <Dialog
        open={showNoRecordDialog}
        onClose={() => {
          setShowNoRecordDialog(false);
          // Opcional: redirigir al usuario a la página principal
          // window.location.href = '/health';
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bg
              : undefined,
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
          }}
        >
          <AlertCircle size={24} color="#FFA726" />
          {t('noMedicalRecord.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.textSecondary
                : undefined
            }}
          >
            {t('noMedicalRecord.dialog.content', { center: healthCenter?.name })}
            <ul>
              {t('noMedicalRecord.dialog.reasons', { returnObjects: true }).map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
            {t('noMedicalRecord.dialog.instruction')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setShowNoRecordDialog(false);
              // Opcional: volver a la selección de centro
              setActiveStep(3);
            }} 
            variant="outlined"
            sx={{ 
              textTransform: 'none',
              borderColor: HEALTH_GREEN,
              color: HEALTH_GREEN,
              '&:hover': {
                borderColor: '#3d8b40',
              },
              borderRadius: 10,
              mr: 1
            }}
          >
            {t('noMedicalRecord.dialog.selectDifferent')}
          </Button>
          <Button 
            onClick={() => {
              setShowNoRecordDialog(false);
              // Opcional: redirigir al usuario a la página principal
              window.location.href = '/health';
            }} 
            variant="contained"
            sx={{ 
              textTransform: 'none',
              backgroundColor: HEALTH_GREEN,
              '&:hover': {
                backgroundColor: '#3d8b40',
              },
              borderRadius: 10,
            }}
          >
            {t('noMedicalRecord.dialog.returnHome')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for successful center request */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bg
              : undefined,
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
          }
        }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
          }}
        >
          {t('requestCenter.success.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.textSecondary
                : undefined
            }}
          >
            {t('requestCenter.success.content', { center: customCenterName })}

            <Box sx={{ mt: 2 }}>
              {t('requestCenter.success.additionalInfo')}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setShowSuccessDialog(false);
            }} 
            variant="contained"
            sx={{ 
              textTransform: 'none',
              backgroundColor: HEALTH_GREEN,
              '&:hover': {
                backgroundColor: '#3d8b40',
              },
              borderRadius: 10,
            }}
          >
            {t('requestCenter.success.button')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for correcting detected center */}
      <Dialog
        open={showCorrectionDialog}
        onClose={() => setShowCorrectionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bg
              : undefined,
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
          }
        }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
          }}
        >
          {t('correctCenter.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              mb: 3,
              color: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.textSecondary
                : undefined
            }}
          >
            {t('correctCenter.dialog.content')}
          </DialogContentText>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              fontWeight={500}
              sx={{
                mb: 1,
                color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
              }}
            >
              {t('correctCenter.dialog.currentlySelected')}
            </Typography>
            <Alert
              sx={{ 
                mb: 2,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(227, 227, 227, 0.5)',
                overflow: 'hidden',
                backgroundColor: '#fff',
                padding: 0
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
              }}>
                <span style={{
                  fontWeight: 500,
                  fontSize: '15px',
                  color: '#000000'
                }}>
                  {t('healthCenter.information')}
                </span>
              </div>
              <div style={{
                padding: '16px 20px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  lineHeight: 1.5,
                  color: '#333333'
                }}>
                  {healthCenter?.name}
                </p>
              </div>
            </Alert>
          </Box>


          {alternativeCenters.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={500}
                sx={{
                  mb: 1,
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
                }}
              >
                {t('correctCenter.dialog.otherCenters')}
              </Typography>
              <List disablePadding>
                {alternativeCenters.map((center, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => handleSelectAlternativeCenter(center)}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: theme.palette.actualMode === 'wallpaper'
                        ? WALLPAPER_COLORS.bgLight
                        : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7'),
                      color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined,
                    }}
                  >
                    <ListItemText primary={center.name} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setShowCorrectionDialog(false)} 
            sx={{ 
              textTransform: 'none',
              color: theme.palette.text.primary
            }}
          >
            {t('correctCenter.dialog.keepCurrent')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Config;
